<script setup lang="ts">
import { computed, inject, watch, onUnmounted, ref, nextTick } from 'vue'
import { AlertTriangle, Info, XCircle, CheckCircle } from 'lucide-vue-next'
import type { DialogStore } from './index'

const dialogStore = inject<DialogStore>('dialogStore')
const confirmBtnRef = ref<HTMLButtonElement | null>(null)

// 확인/취소 어느 쪽으로 닫든 콜백을 모두 비워, 닫힌 다이얼로그의 콜백이 다음 다이얼로그로 넘어가지 않게 한다.
const hideDialog = () => {
  if (!dialogStore) return
  dialogStore.dialog = false
  dialogStore.callback = null
  dialogStore.cancelCallback = null
  dialogStore.processing = false
}

const closeDialog = async () => {
  if (!dialogStore || dialogStore.processing) return
  const cb = dialogStore.callback

  // 알림(success/error 등)의 확인: 기존처럼 먼저 닫고 콜백 실행
  if (!cb || !dialogStore.cancelCallback) {
    hideDialog()
    if (cb) {
      nextTick(() => {
        setTimeout(() => {
          cb()
        }, 30)
      })
    }
    return
  }

  // confirm의 확인: 콜백을 먼저 실행하고, 비동기면 끝날 때까지 "처리 중..."으로 열어 둔다.
  // 그동안 확인창이 화면을 덮고 있어 원래 버튼도 다시 누를 수 없다.
  const seq = dialogStore.seq
  let result: unknown
  try {
    result = cb()
  } catch (err) {
    console.error('Dialog confirm callback failed:', err)
  }
  if (result instanceof Promise) {
    dialogStore.processing = true
    try {
      await result
    } catch (err) {
      console.error('Dialog confirm callback failed:', err)
    }
  }
  // 콜백 안에서 결과 알림 등 새 다이얼로그를 띄웠으면(seq 변경) 그 다이얼로그는 닫지 않는다.
  if (dialogStore.seq === seq) hideDialog()
}

const cancelDialog = () => {
  if (dialogStore?.processing) return
  const cancelCb = dialogStore?.cancelCallback
  if (dialogStore) {
    dialogStore.dialog = false
    dialogStore.callback = null
    dialogStore.cancelCallback = null
  }
  // 상태를 먼저 비운 뒤 실행해야 cancelCb 안에서 새 다이얼로그를 띄워도 닫히거나 콜백이 지워지지 않는다.
  cancelCb?.()
}

const handleKeydown = (e: KeyboardEvent) => {
  if (!dialogStore?.dialog) return
  if (e.key === 'Enter') {
    e.preventDefault()
    e.stopPropagation()
    closeDialog()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    cancelDialog()
  }
}

watch(
  () => dialogStore?.dialog,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeydown, true)
      nextTick(() => {
        confirmBtnRef.value?.focus()
      })
    } else {
      window.removeEventListener('keydown', handleKeydown, true)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown, true)
})

const config = computed(() => {
  switch (dialogStore?.color) {
    case 'warning':
      return {
        icon: AlertTriangle,
        iconColor: '#F59E0B',
        iconBg: '#FEF3C7',
        confirmClass: 'btn-warning',
      }
    case 'red-lighten-1':
      return {
        icon: XCircle,
        iconColor: '#EF4444',
        iconBg: '#FEE2E2',
        confirmClass: 'btn-danger',
      }
    case 'success':
      return {
        icon: CheckCircle,
        iconColor: '#10B981',
        iconBg: '#D1FAE5',
        confirmClass: 'btn-success',
      }
    default: // info
      return {
        icon: Info,
        iconColor: '#6366F1',
        iconBg: '#EEF2FF',
        confirmClass: 'btn-info',
      }
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div v-if="dialogStore?.dialog" class="dialog-backdrop">
        <div class="dialog-panel">
          <!-- Icon -->
          <div class="dialog-icon-wrap" :style="{ backgroundColor: config.iconBg }">
            <component :is="config.icon" :size="28" :color="config.iconColor" :stroke-width="2" />
          </div>

          <!-- Content -->
          <div class="dialog-content">
            <h3 class="dialog-title">{{ dialogStore!.title }}</h3>
            <p class="dialog-msg">{{ dialogStore!.msg }}</p>
          </div>

          <!-- Actions -->
          <div class="dialog-actions">
            <button
              v-if="dialogStore!.cancelCallback"
              class="dialog-btn btn-cancel"
              :disabled="dialogStore!.processing"
              @click="cancelDialog"
            >
              취소
            </button>
            <button
              ref="confirmBtnRef"
              class="dialog-btn"
              :class="config.confirmClass"
              :disabled="dialogStore!.processing"
              @click="closeDialog"
            >
              {{ dialogStore!.processing ? '처리 중...' : '확인' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.dialog-backdrop {
  position: fixed;
  inset: 0;
  /* 확인·알림 다이얼로그는 어떤 창 위에서 띄워도 맨 앞에 보여야 한다.
     일반 창(z-index 9999)과 그 안의 드롭다운(10000)보다 위, 토스트 알림(SimpleAlert, 20000)보다는 아래 */
  z-index: 15000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
}

.dialog-panel {
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 32px 28px 24px;
  width: 360px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.dialog-icon-wrap {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dialog-content {
  text-align: center;
}

.dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.dialog-msg {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.6;
  white-space: pre-wrap;
}

.dialog-actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
}

.dialog-btn {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

/* confirm 처리 중 (확인 콜백 대기) */
.dialog-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background-color: #F3F4F6;
  color: #374151;
}
.btn-cancel:hover {
  background-color: #E5E7EB;
}

.btn-info {
  background-color: #0D1B3E;
  color: #ffffff;
}
.btn-info:hover {
  background-color: #1a2d5a;
}

.btn-warning {
  background-color: #F59E0B;
  color: #ffffff;
}
.btn-warning:hover {
  background-color: #D97706;
}

.btn-danger {
  background-color: #EF4444;
  color: #ffffff;
}
.btn-danger:hover {
  background-color: #DC2626;
}

.btn-success {
  background-color: #10B981;
  color: #ffffff;
}
.btn-success:hover {
  background-color: #059669;
}

/* Transition */
.dialog-enter-active,
.dialog-leave-active {
  transition: all 0.2s ease;
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}
.dialog-enter-from .dialog-panel,
.dialog-leave-to .dialog-panel {
  transform: scale(0.95) translateY(8px);
}
</style>
